import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/api';

const JokeOfTheDay = () => {
  const [joke, setJoke] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  const fetchJoke = useCallback(() => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/jokes/daily`, true);
    x.onload = function () {
      if (x.status === 200) {
        try { setJoke(JSON.parse(x.responseText)); } catch (e) {}
      }
    };
    x.send();
  }, []);

  useEffect(() => { fetchJoke(); }, [fetchJoke]);

  const getNextRandom = () => {
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/jokes/random`, true);
    x.onload = function () {
      if (x.status === 200) {
        try {
          const data = JSON.parse(x.responseText);
          setJoke(prev => ({ ...prev, joke: data.joke }));
        } catch (e) {}
      }
    };
    x.send();
  };

  const copyJoke = async () => {
    if (!joke?.joke) return;
    const text = `"${joke.joke.text}"\n\n\u2014 from Letters to Evelyn by John Selman\nGet the book: https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {}
  };

  const shareToTwitter = () => {
    if (!joke?.joke) return;
    const t = encodeURIComponent(`"${joke.joke.text}"\n\n\u2014 Letters to Evelyn by John Selman`);
    window.open(`https://twitter.com/intent/tweet?text=${t}`, '_blank');
    setShowShareMenu(false);
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent('https://play.google.com/store/books/details/John_Selman_Letters_to_Evelyn?id=KpByEQAAQBAJ&hl=en_US');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
    setShowShareMenu(false);
  };

  if (!joke?.joke) return null;

  const j = joke.joke;
  const catColors = {
    'Fighter Pilot': '#60a5fa', 'Survival': '#f87171', 'Military': '#34d399',
    'Letters to Evelyn': '#047857', 'Technology': '#22d3ee', 'Existential': '#fbbf24',
    'Family': '#fb923c', 'Bureaucracy': '#047857', 'Cosmic': '#818cf8',
    'InfoPilot': '#FEF08A', 'Observational': '#94a3b8', 'Book Excerpt': '#e879f9',
    'Aviation Heritage': '#38bdf8', 'Career Sabotage': '#ef4444', 'Book Promo': '#4ade80',
  };
  const color = catColors[j.category] || '#047857';

  return (
    <div data-testid="joke-of-the-day" style={{
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(253, 251, 247, 0.95))',
      border: `1px solid ${color}40`,
      borderRadius: 16,
      marginBottom: 16,
      overflow: 'hidden',
      transition: 'all 0.3s',
    }}>
      {/* Header — always visible */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 18px', cursor: 'pointer',
          background: `linear-gradient(90deg, ${color}18, transparent)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="fas fa-laugh-squint" style={{ color, fontSize: '1.1rem' }} />
          <span style={{ color: '#0F172A', fontWeight: 700, fontSize: '0.92rem' }}>
            Joke of the Day
          </span>
          <span style={{
            background: `${color}25`, color, padding: '2px 10px',
            borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
          }}>
            {j.category}
          </span>
        </div>
        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`}
           style={{ color: '#71717a', fontSize: '0.8rem' }} />
      </div>

      {/* Body */}
      {expanded && (
        <div style={{ padding: '4px 18px 16px' }}>
          <p data-testid="joke-text" style={{
            color: '#334155', fontSize: '1.02rem', lineHeight: 1.7,
            fontStyle: 'italic', margin: '0 0 12px',
          }}>
            "{j.text}"
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ color: '#71717a', fontSize: '0.78rem' }}>
              {'\u2014'} {j.author}, <em>Letters to Evelyn</em>
            </span>
            <div style={{ display: 'flex', gap: 6, position: 'relative' }}>
              <button data-testid="joke-next-btn" onClick={getNextRandom}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '6px 12px', color: '#d4d4d8', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <i className="fas fa-random" /> Another
              </button>
              <button data-testid="joke-copy-btn" onClick={copyJoke}
                style={{ background: copied ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)', border: `1px solid ${copied ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.12)'}`, borderRadius: 8, padding: '6px 12px', color: copied ? '#10b981' : '#d4d4d8', fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <i className={copied ? 'fas fa-check' : 'fas fa-copy'} /> {copied ? 'Copied!' : 'Copy'}
              </button>
              <div style={{ position: 'relative' }}>
                <button data-testid="joke-share-btn" onClick={() => setShowShareMenu(!showShareMenu)}
                  style={{ background: `${color}15`, border: `1px solid ${color}30`, borderRadius: 8, padding: '6px 12px', color, fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <i className="fas fa-share-alt" /> Share
                </button>
                {showShareMenu && (
                  <div style={{
                    position: 'absolute', right: 0, top: '110%', zIndex: 50,
                    background: 'rgba(255, 255, 255, 0.98)', border: '1px solid rgba(255,255,255,0.15)',
                    borderRadius: 10, padding: 6, minWidth: 160, boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
                  }}>
                    <button onClick={shareToTwitter}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: '#1DA1F2', fontSize: '0.82rem', cursor: 'pointer', borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(29,161,242,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <i className="fab fa-twitter" /> Twitter
                    </button>
                    <button onClick={shareToFacebook}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: '#4267B2', fontSize: '0.82rem', cursor: 'pointer', borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(66,103,178,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <i className="fab fa-facebook" /> Facebook
                    </button>
                    <button onClick={copyJoke}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', color: '#d4d4d8', fontSize: '0.82rem', cursor: 'pointer', borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <i className="fas fa-link" /> Copy Link
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JokeOfTheDay;
